import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { apiFetch, apiPost, friendlyError, type ApiError } from "../../api/client.js";
import { QuotaBar } from "./quota-bar.js";
import { isVmDataDisk } from "./vmUtils.js";

interface CreateOptions {
  quota: { cpu_cores: number; ram_mb: number; disk_gb: number };
  usage: { cpu_cores: number; ram_mb: number; disk_gb: number } | null;
  usage_unavailable?: boolean;
  remaining: { cpu_cores: number; ram_mb: number; disk_gb: number } | null;
}

interface ResizeDiskOption {
  slot: string;
  size_bytes: number;
  media?: string | null;
}

interface ResizeVmModalProps {
  vmid: number;
  current: { cores: number; memory_mb: number; disk_gb: number };
  /** Phase 26: when the VM has >1 data disk, offer a per-disk picker and
   * send `disk_slot` in the resize body. Omitted → legacy boot-disk
   * behavior (single input, no disk_slot field sent). */
  disks?: ResizeDiskOption[];
  bootSlot?: string | null;
  opened: boolean;
  onClose: () => void;
  onChanged: () => void;
}

/*
 * Resize VM (Phase 7b). Fetches /api/create-options on open to read the
 * customer's quota + remaining headroom, then offers three inputs: CPU
 * cores (absolute), memory (absolute, MB/GB toggle), and disk growth
 * (positive delta in GB). The backend computes deltas and enforces quota
 * server-side. The live quota bars show PROJECTED usage (what the total
 * will be after the resize), updating as the customer edits.
 *
 * Phase 26: with `disks` present and >1 data disk, a Select picks the
 * target disk (default boot); the delta then applies to that disk and
 * `disk_slot` is sent. The projection stays `usage + delta` either way
 * (the backend quota math is disk-agnostic).
 *
 * Error mapping (matches the backend's response shapes):
 *   400 no_change        -> "No changes to apply."
 *   400 no_data_disk     -> "This VM has no resizable disk."
 *   409 quota_exceeded   -> red "Quota exceeded: <field> +<excess>"
 *   409 vm_locked        -> "Another operation is in progress..."
 *   404 disk_not_found   -> "The selected disk no longer exists."
 *   502 pve_unavailable  -> "Proxmox is unavailable. Please try again."
 *   502 partial          -> orange "Some changes applied but Proxmox
 *                            reported a partial failure." then refetch.
 */
export function ResizeVmModal({
  vmid,
  current,
  disks,
  bootSlot,
  opened,
  onClose,
  onChanged,
}: ResizeVmModalProps) {
  const [options, setOptions] = useState<CreateOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [cores, setCores] = useState<number | "">("");
  const [memoryMb, setMemoryMb] = useState<number | "">("");
  const [diskDelta, setDiskDelta] = useState<number | "">("");
  const [diskSlot, setDiskSlot] = useState<string | null>(null);
  const [memUnit, setMemUnit] = useState<"mb" | "gb">("gb");

  async function loadOptions() {
    setLoading(true);
    try {
      const data = await apiFetch<CreateOptions>("/api/create-options");
      setOptions(data);
      setCores(current.cores);
      setMemoryMb(current.memory_mb);
      setDiskDelta(0);
      setMemUnit("gb");
      const pick = (disks ?? []).filter(isVmDataDisk);
      if (pick.length > 1) {
        setDiskSlot(
          bootSlot && pick.some((d) => d.slot === bootSlot)
            ? bootSlot
            : (pick[0]?.slot ?? null)
        );
      } else {
        setDiskSlot(null);
      }
    } catch (err) {
      notifications.show({
        color: "red",
        message: friendlyError(err, "Could not load resize options."),
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (opened) {
      void loadOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const dataDisks = (disks ?? []).filter(isVmDataDisk);
  const showDiskPicker = dataDisks.length > 1;
  const selectedDisk =
    dataDisks.find((d) => d.slot === diskSlot) ?? dataDisks[0] ?? null;

  const usageUnavailable = !!options?.usage_unavailable;
  const usage = options?.usage ?? { cpu_cores: 0, ram_mb: 0, disk_gb: 0 };
  const remaining = options?.remaining ?? { cpu_cores: 0, ram_mb: 0, disk_gb: 0 };
  const quota = options?.quota ?? { cpu_cores: 0, ram_mb: 0, disk_gb: 0 };

  const newCores = typeof cores === "number" ? cores : current.cores;
  const newMemoryMb = typeof memoryMb === "number" ? memoryMb : current.memory_mb;
  const newDelta = typeof diskDelta === "number" ? diskDelta : 0;

  const maxCores = Math.min(64, quota.cpu_cores);
  const maxRamMb = Math.min(262144, current.memory_mb + remaining.ram_mb);
  const maxDiskDelta = remaining.disk_gb;

  const projectedRam = usage.ram_mb - current.memory_mb + newMemoryMb;
  const projectedDisk = usage.disk_gb + newDelta;

  const coresChanged = typeof cores === "number" && cores !== current.cores;
  const memChanged = typeof memoryMb === "number" && memoryMb !== current.memory_mb;
  const diskChanged = typeof diskDelta === "number" && diskDelta !== 0;
  const changed = coresChanged || memChanged || diskChanged;

  const overCores = newCores > quota.cpu_cores;
  const overRam = projectedRam > quota.ram_mb;
  const overDisk = projectedDisk > quota.disk_gb;

  const canSubmit =
    !submitting &&
    !usageUnavailable &&
    changed &&
    !overCores &&
    !overRam &&
    !overDisk;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    const body: Record<string, unknown> = {};
    if (coresChanged) body.cores = newCores;
    if (memChanged) body.memory_mb = newMemoryMb;
    if (diskChanged) {
      body.disk_gb_delta = newDelta;
      if (showDiskPicker && diskSlot) body.disk_slot = diskSlot;
    }
    try {
      await apiPost<{ vmid: number; cores: number; memory_mb: number; disk_gb: number }>(
        `/api/vms/${vmid}/resize`,
        body
      );
      notifications.show({ color: "green", message: "VM resized." });
      onChanged();
      onClose();
    } catch (err) {
      const e = err as ApiError;
      const detail = e.details as Record<string, unknown> | undefined;
      if (e.status === 400 && e.error === "no_change") {
        notifications.show({ color: "red", message: "No changes to apply." });
      } else if (e.status === 400 && e.error === "no_data_disk") {
        notifications.show({ color: "red", message: "This VM has no resizable disk." });
      } else if (e.status === 409 && e.error === "quota_exceeded") {
        const excess = detail?.excess as Record<string, number> | undefined;
        const fields: string[] = [];
        if (excess?.cpu_cores) fields.push(`CPU +${excess.cpu_cores}`);
        if (excess?.ram_mb) fields.push(`RAM +${excess.ram_mb} MB`);
        if (excess?.disk_gb) fields.push(`Disk +${excess.disk_gb} GB`);
        notifications.show({
          color: "red",
          message: `Quota exceeded: ${fields.join(", ") || "insufficient headroom"}.`,
        });
      } else if (e.status === 409 && e.error === "vm_locked") {
        notifications.show({
          color: "red",
          message: "Another operation is in progress; try again in a moment.",
        });
      } else if (e.status === 404 && e.error === "disk_not_found") {
        notifications.show({ color: "red", message: "The selected disk no longer exists." });
        onChanged();
      } else if (e.status === 502 && e.error === "pve_unavailable" && !!detail?.partial) {
        notifications.show({
          color: "orange",
          message:
            "Some changes applied but Proxmox reported a partial failure. Refresh to see current state.",
        });
        onChanged();
        onClose();
      } else if (e.status === 502 && e.error === "pve_unavailable") {
        notifications.show({
          color: "red",
          message: "Proxmox is unavailable. Please try again.",
        });
      } else {
        notifications.show({ color: "red", message: friendlyError(e, "Resize failed.") });
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Memory input: display in the chosen unit but store MB in state.
  const memDisplay =
    memUnit === "gb" && typeof memoryMb === "number" ? memoryMb / 1024 : memoryMb;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Resize VM"
      size="lg"
      closeOnClickOutside={!submitting}
      closeOnEscape={!submitting}
    >
      <Stack gap="md">
        {loading && (
          <Text c="dimmed" size="sm">
            Loading options…
          </Text>
        )}

        {options && usageUnavailable && (
          <Alert color="yellow" variant="light" p="sm">
            Can't compute current usage (Proxmox unreachable). Please try again later.
          </Alert>
        )}

        {options && !usageUnavailable && (
          <>
            <Alert color="gray" variant="light" p="sm">
              <Stack gap="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Projected usage after resize
                </Text>
                <QuotaBar label="Memory" used={projectedRam} total={quota.ram_mb} unit="MB" />
                <QuotaBar label="Disk" used={projectedDisk} total={quota.disk_gb} unit="GB" />
              </Stack>
            </Alert>

            <NumberInput
              label="CPU cores"
              description={`Current: ${current.cores}`}
              value={cores}
              onChange={(v) => setCores(typeof v === "number" ? v : "")}
              min={1}
              max={maxCores}
              error={overCores ? `Up to ${maxCores} cores per VM.` : undefined}
              disabled={submitting}
            />

            <Stack gap={4}>
              <Group justify="space-between" align="flex-end">
                <Box style={{ flex: 1 }}>
                  <NumberInput
                    label={memUnit === "gb" ? "Memory (GB)" : "Memory (MB)"}
                    description={`Current: ${formatRamBrief(current.memory_mb)}`}
                    value={memDisplay}
                    onChange={(v) => {
                      const n = typeof v === "number" ? v : 0;
                      setMemoryMb(memUnit === "gb" ? Math.round(n * 1024) : n);
                    }}
                    min={memUnit === "gb" ? 1 : 128}
                    max={
                      memUnit === "gb"
                        ? Math.min(256, Math.floor(maxRamMb / 1024) || 1)
                        : Math.min(262144, Math.max(128, maxRamMb))
                    }
                    error={overRam ? `Only ${maxRamMb} MB allowed by quota.` : undefined}
                    disabled={submitting}
                  />
                </Box>
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() => setMemUnit((u) => (u === "gb" ? "mb" : "gb"))}
                  disabled={submitting}
                >
                  Switch to {memUnit === "gb" ? "MB" : "GB"}
                </Button>
              </Group>
            </Stack>

            {showDiskPicker && (
              <Select
                label="Disk"
                description="Choose which disk to grow"
                value={diskSlot}
                onChange={setDiskSlot}
                data={dataDisks.map((d) => ({
                  value: d.slot,
                  label: `${d.slot} — ${Math.round(d.size_bytes / 1024 ** 3)} GB`,
                }))}
                disabled={submitting}
              />
            )}

            <NumberInput
              label="Add disk (GB)"
              description={`Current disk: ${
                showDiskPicker && selectedDisk
                  ? Math.round(selectedDisk.size_bytes / 1024 ** 3)
                  : current.disk_gb
              } GB`}
              value={diskDelta}
              onChange={(v) => setDiskDelta(typeof v === "number" ? v : "")}
              min={0}
              max={maxDiskDelta}
              error={overDisk ? `Only ${maxDiskDelta} GB remaining.` : undefined}
              disabled={submitting}
            />

            <Group justify="flex-end" mt="xs">
              <Button variant="default" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={() => void submit()} loading={submitting} disabled={!canSubmit}>
                Apply
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}

function formatRamBrief(mb: number): string {
  if (mb >= 1024 && mb % 1024 === 0) return `${mb / 1024} GB`;
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb} MB`;
}
